import React, { useContext, useEffect, useState } from 'react';

import IconRecommendEmpty from '@digital-hig/icon/icons/thumbs-up';
import IconRecommendFilled from '@digital-hig/icon/icons/thumbs-up-filled';
import SvgIcon from '@digital-hig/mui/SvgIcon';
import { useTypedTranslation } from 'auonline2-next/lib/useTypedTranslation';
import cn from 'classnames';

import twConfig from '../../../../tailwind.config';
import {
  useKhorosKudos,
  useKhorosKudoSet,
  useKhorosKudoUnset,
  useKhorosTopic,
} from '../../../apis/khoros/hooks';
import { KhorosTopicProps } from '../../../apis/khoros/interface/khoros.interface';
import {
  Tooltip,
  TooltipContent,
  TooltipInfoContent,
  TooltipTrigger,
} from '../../../components/presentational/tooltip/Tooltip';
import extractErrorMessage from '../../../helpers/extractErrorMessage';
import LoginContext from '../../contexts/LoginContext';

const white = twConfig.theme.colors.white || '';

interface RecommendationProps extends KhorosTopicProps {
  theme: 'horizontal' | 'horizontal-condensed' | 'vertical';
  className?: string | object;
}

interface RecommendEvent {
  readonly khorosTopicId: string;
  readonly count: number | null;
  readonly oxygenId: string;
  readonly action: string;
}

interface RecommendError {
  readonly message: string;
  readonly cause: 'manual' | 'api';
}

export default function Recommendation({
  baseURL,
  topicId,
  theme = 'vertical',
  className,
}: RecommendationProps): JSX.Element {
  const topicProps = {
    baseURL,
    topicId,
  };

  const { userProfile, isLoggedIn } = useContext(LoginContext);

  const { isLoading: kudosIsLoading, isAuthor } = useKhorosKudos(topicProps);
  const { data: topicData, isLoading: topicIsLoading } =
    useKhorosTopic(topicProps);
  const [error, setError] = useState<RecommendError | null>(null);
  const [hovered, setHovered] = useState(false);

  const { t } = useTypedTranslation(['common']);

  const {
    isPending: applyRecommendationLoading,
    mutateAsync: applyRecommendation,
    error: applyRecommendationError,
    isError: applyRecommendationIsError,
    reset: applyRecommendationReset,
  } = useKhorosKudoSet(topicProps);
  const {
    isPending: unapplyRecommendationLoading,
    mutateAsync: unapplyRecommendation,
    error: unapplyRecommendationError,
    isError: unapplyRecommendationIsError,
    reset: unapplyRecommendationReset,
  } = useKhorosKudoUnset(topicProps);

  useEffect(() => {
    if (applyRecommendationIsError || unapplyRecommendationIsError) {
      // Handle extra use case of server returning the message in the "detail" prop
      let receivedErrorDetail = '';
      if (applyRecommendationIsError) {
        receivedErrorDetail = extractErrorMessage(applyRecommendationError);
      } else if (unapplyRecommendationIsError) {
        receivedErrorDetail = extractErrorMessage(unapplyRecommendationError);
      }

      setError({
        message:
          receivedErrorDetail || t('recommendations.error', { ns: 'common' }),
        cause: 'api',
      });
    } else if (error?.cause === 'api') {
      setError(null);
    }
  }, [
    applyRecommendationIsError,
    unapplyRecommendationIsError,
    applyRecommendationError,
    unapplyRecommendationError,
  ]);

  const isLoading = topicIsLoading || kudosIsLoading;

  const currentlyLiked = topicData?.kh_context_user?.kudoed || false;

  let count: number | null = null;
  if (typeof topicData?.kh_kudos_count === 'number') {
    count = topicData.kh_kudos_count;
  }

  const disabled =
    isLoading || applyRecommendationLoading || unapplyRecommendationLoading;

  const callCustomEvent = (countOffset: number) => {
    if (count === null || typeof window === 'undefined') {
      return;
    }

    const customEvent = new CustomEvent<RecommendEvent>('RecommendEvent', {
      detail: {
        oxygenId: userProfile ? userProfile.sub : '',
        khorosTopicId: topicId,
        count: count + countOffset,
        action: countOffset > 0 ? 'like' : 'unlike',
      },
    });

    window.dispatchEvent(customEvent);
  };

  const recommendActionHandler = () => {
    if (!isLoggedIn) {
      setError({
        message: t('error_401', { ns: 'common' }),
        cause: 'manual',
      });
      return;
    }
    if (isAuthor) {
      setError({
        message: t('recommendations.error_author', { ns: 'common' }),
        cause: 'manual',
      });
      return;
    }
    if (currentlyLiked) {
      unapplyRecommendation().then(() => callCustomEvent(-1));
    } else {
      applyRecommendation().then(() => callCustomEvent(1));
    }
  };

  const errorCloseHandler = () => {
    if (error?.cause === 'manual') {
      setError(null);
    } else if (applyRecommendationIsError) {
      applyRecommendationReset();
    } else if (unapplyRecommendationIsError) {
      unapplyRecommendationReset();
    }
  };

  let displayCount: string | number = '';
  if (typeof count === 'number') {
    displayCount = count;
  } else if (!isLoading) {
    displayCount = 0;
  }

  return (
    <Tooltip open={!!error} onOpenChange={errorCloseHandler}>
      <TooltipTrigger
        type="button"
        onClick={recommendActionHandler}
        disabled={disabled}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={cn(
          'au-recommend au-flex au-items-center au-text-c18 au-leading-large au-text-gray-600 au-font-element au-text-white',
          className,
          {
            'au-flex-col au-gap-c5 au-recommend--vertical':
              theme === 'vertical',
            'au-gap-c10 au-recommend--horizontal':
              theme === 'horizontal' || theme === 'horizontal-condensed',
            'au-recommend--has-error': error,
            'au-opacity-25': disabled,
          }
        )}
      >
        {currentlyLiked || hovered ? (
          <SvgIcon fontSize="large" className="au-w-c20 au-h-c20">
            <IconRecommendFilled fill={white} color={white} />
          </SvgIcon>
        ) : (
          <SvgIcon fontSize="large" className="au-w-c20 au-h-c20" fill={white}>
            <IconRecommendEmpty stroke={white} color={white} />
          </SvgIcon>
        )}
        {theme === 'horizontal' && (
          <>
            {t('recommendations.recommend').concat(
              displayCount ? ` (${displayCount})` : ''
            )}
          </>
        )}
        {theme === 'horizontal-condensed' && (displayCount || '')}
        {theme === 'vertical' && displayCount && `${displayCount}`}
      </TooltipTrigger>
      <TooltipContent>
        <TooltipInfoContent>{error?.message}</TooltipInfoContent>
      </TooltipContent>
    </Tooltip>
  );
}

Recommendation.defaultProps = {};

Recommendation.propTypes = {};

Recommendation.attributes = {
  baseURL: {
    type: 'string',
    default: 'https://api.au-uw2-prd.autodesk.com/',
  },
  topicId: {
    type: 'string',
    default: '',
  },
  theme: {
    type: 'string',
    default: 'horizontal',
  },
  className: {
    type: 'string',
    default: '',
  },
};
